/* ============================================================
   Modal.jsx — the js/ui.js modal, as a portal.

   Same markup and class names. Adds the two things the original lacked and that
   01-repository-assessment.md §5 item 9 calls out: a focus trap, and focus
   restored to whatever opened the modal. Closing the prototype's modal dropped
   focus to <body>, which strands keyboard and screen-reader users.

   Body scroll locking is reference-counted, because the mobile nav drawer also
   sets body.overflow — without the count, closing a modal while the drawer is
   open would unlock scrolling underneath it.
   ============================================================ */

import { useCallback, useEffect, useLayoutEffect, useRef } from 'react'
import { createPortal } from 'react-dom'
import Icon from './icons'

let scrollLocks = 0
function lockScroll() {
  scrollLocks += 1
  document.body.style.overflow = 'hidden'
}
function unlockScroll() {
  scrollLocks = Math.max(0, scrollLocks - 1)
  if (scrollLocks === 0) document.body.style.overflow = ''
}

// Open modals, newest last: Escape closes only the top one (a confirm popup
// over a form must not close the form underneath with it).
const openStack = []

const FOCUSABLE =
  'a[href], button:not([disabled]), textarea:not([disabled]), input:not([disabled]), select:not([disabled]), [tabindex]:not([tabindex="-1"])'

export default function Modal({ open = true, title, subtitle, size, footer, onClose, children }) {
  const dialogRef = useRef(null)
  const restoreRef = useRef(null)

  // Latest onClose in a ref: an inline onClose changes every parent render, and
  // re-running the effect below would move this modal to the top of openStack.
  const onCloseRef = useRef(onClose)
  useLayoutEffect(() => { onCloseRef.current = onClose })
  const close = useCallback(() => onCloseRef.current?.(), [])

  useEffect(() => {
    if (!open) return undefined
    restoreRef.current = document.activeElement
    lockScroll()

    const node = dialogRef.current
    const first = node?.querySelector(FOCUSABLE)
    ;(first ?? node)?.focus?.()
    const token = {}
    openStack.push(token)

    const onKeyDown = (e) => {
      if (openStack[openStack.length - 1] !== token) return
      if (e.key === 'Escape') {
        // An open ui/Select menu takes the Escape itself; the modal stays open.
        if (e.target.closest?.('.ss-menu')) return
        e.stopPropagation()
        close()
        return
      }
      if (e.key !== 'Tab' || !node) return
      const items = Array.from(node.querySelectorAll(FOCUSABLE)).filter(
        (el) => el.offsetParent !== null,
      )
      if (!items.length) return
      const firstEl = items[0]
      const lastEl = items[items.length - 1]
      if (e.shiftKey && document.activeElement === firstEl) {
        e.preventDefault()
        lastEl.focus()
      } else if (!e.shiftKey && document.activeElement === lastEl) {
        e.preventDefault()
        firstEl.focus()
      }
    }

    document.addEventListener('keydown', onKeyDown, true)
    return () => {
      document.removeEventListener('keydown', onKeyDown, true)
      openStack.splice(openStack.indexOf(token), 1)
      unlockScroll()
      restoreRef.current?.focus?.()
    }
  }, [open, close])

  if (!open) return null

  return createPortal(
    <div className="modal-root open">
      <div className="modal-backdrop" onClick={close} />
      <div
        className={`modal ${size || ''}`}
        role="dialog"
        aria-modal="true"
        aria-label={typeof title === 'string' ? title : undefined}
        tabIndex={-1}
        ref={dialogRef}
      >
        <div className="modal-head">
          <div>
            <h2>{title}</h2>
            {subtitle && <p>{subtitle}</p>}
          </div>
          <button className="modal-close" onClick={close} aria-label="Close">
            <Icon name="x" />
          </button>
        </div>
        <div className="modal-body">{children}</div>
        {footer && <div className="modal-foot">{footer}</div>}
      </div>
    </div>,
    document.body,
  )
}
